import type { SyncOperation, SyncStatus } from '@/domain/types';

export interface SyncDiagOp {
  id: string;
  entityType: SyncOperation['entityType'];
  entityId: string;
  action: SyncOperation['action'];
  attempts: number;
  nextRetryAt: string | null;
  errorKind: SyncOperation['errorKind'];
  lastError: string | null;
  lastErrorDetail: string | null;
  /** Field names only — never values (titles, descriptions, tokens). */
  payloadKeys: string[];
}

export interface SyncDiagnosticsReport {
  capturedAt: string;
  mode: string;
  online: boolean;
  syncStatus: SyncStatus;
  queueCount: number;
  ops: SyncDiagOp[];
}

/** Strip JWTs / API keys from free-text error details before export. */
export function sanitizeErrorDetail(detail: string | null | undefined): string | null {
  if (!detail) return null;
  return detail
    .replace(/eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]*/g, '[redacted-jwt]')
    .replace(/Bearer\s+\S+/gi, 'Bearer [redacted]')
    .replace(/(?:api[_-]?key|service_role|anon)[=:\s]+[^\s,;]+/gi, '[redacted-key]')
    .slice(0, 400);
}

export function buildSyncDiagnostics(opts: {
  mode: string;
  online: boolean;
  syncStatus: SyncStatus;
  queue: SyncOperation[];
}): SyncDiagnosticsReport {
  return {
    capturedAt: new Date().toISOString(),
    mode: opts.mode,
    online: opts.online,
    syncStatus: opts.syncStatus,
    queueCount: opts.queue.length,
    ops: opts.queue.map((op) => ({
      id: op.id,
      entityType: op.entityType,
      entityId: op.entityId,
      action: op.action,
      attempts: op.attempts,
      nextRetryAt: op.nextRetryAt ?? null,
      errorKind: op.errorKind ?? null,
      lastError: op.lastError ?? null,
      lastErrorDetail: sanitizeErrorDetail(op.lastErrorDetail),
      payloadKeys:
        op.payload && typeof op.payload === 'object'
          ? Object.keys(op.payload as Record<string, unknown>).sort()
          : [],
    })),
  };
}

export async function copySyncDiagnosticsText(report: SyncDiagnosticsReport): Promise<void> {
  const text = JSON.stringify(report, null, 2);
  await navigator.clipboard.writeText(text);
}
